"use client";

import { Button } from "@heroui/button";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/modal";
import { useState } from "react";

import type { LanguageCode } from "@/config/i18n";
import type { Translator } from "@/lib/i18n/translations";
import { createTranslator } from "@/lib/i18n/translations";

import { RatingStars } from "./RatingStars";

/**
 * On phones, dialogs are bottom sheets: full width, flush with the bottom
 * edge and clear of the home indicator. Centered from `sm` up.
 */
export const BOTTOM_SHEET = {
  base: "mx-0 mb-0 max-h-[calc(100dvh-2rem)] rounded-b-none sm:mx-6 sm:my-16 sm:rounded-b-large",
  footer: "pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-4",
};

export interface RatingDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  lang: LanguageCode;
  movieTitle: string;
  /** The user's current rating, if any. */
  currentRating: number | null;
  /** Whether the movie is already watched (rating marks it watched). */
  isWatched: boolean;
  onSave: (rating: number) => void;
  onRemove: () => void;
}

/** Pick (or change, or remove) a 1-10 rating for a movie. */
export function RatingDialog({
  isOpen,
  onOpenChange,
  lang,
  ...props
}: RatingDialogProps) {
  const t = createTranslator(lang);

  return (
    <Modal
      classNames={BOTTOM_SHEET}
      isOpen={isOpen}
      placement="auto"
      scrollBehavior="inside"
      size="md"
      onOpenChange={onOpenChange}
    >
      <ModalContent>
        {(onClose) => <RatingForm {...props} t={t} onClose={onClose} />}
      </ModalContent>
    </Modal>
  );
}

interface RatingFormProps extends Omit<
  RatingDialogProps,
  "isOpen" | "onOpenChange" | "lang"
> {
  t: Translator;
  onClose: () => void;
}

/** Mounted each time the dialog opens, so the draft starts fresh. */
function RatingForm({
  t,
  movieTitle,
  currentRating,
  isWatched,
  onSave,
  onRemove,
  onClose,
}: RatingFormProps) {
  const [draft, setDraft] = useState<number | null>(currentRating);
  const [preview, setPreview] = useState<number | null>(null);
  const shown = preview ?? draft;

  return (
    <>
      <ModalHeader className="flex flex-col gap-1">
        <span>{t("library.rating.dialogTitle")}</span>
        <span className="text-sm font-normal text-default-500">
          {movieTitle}
        </span>
      </ModalHeader>
      <ModalBody className="items-center gap-4 py-4">
        <RatingStars
          autoFocus
          label={t("library.rating.groupLabel", { title: movieTitle })}
          starLabel={(rating) => t("library.rating.star", { rating })}
          value={draft}
          onChange={setDraft}
          onPreviewChange={setPreview}
        />
        <p className="text-center tabular-nums" data-testid="rating-preview">
          {shown === null ? (
            <span className="text-lg text-default-500">
              {t("library.rating.noValue")}
            </span>
          ) : (
            <>
              <span className="text-4xl font-bold text-warning">{shown}</span>
              <span className="text-2xl text-default-500">/10</span>
            </>
          )}
        </p>
        <p className="text-center text-xs text-default-500">
          {isWatched
            ? t("library.rating.hint")
            : t("library.rating.marksWatched")}
        </p>
      </ModalBody>
      {/* Phones: full-width buttons, primary on top. */}
      <ModalFooter className="flex-col-reverse sm:flex-row">
        {currentRating !== null && (
          <Button
            className="h-11 w-full sm:mr-auto sm:h-10 sm:w-auto"
            color="danger"
            variant="light"
            onPress={() => {
              onRemove();
              onClose();
            }}
          >
            {t("library.rating.remove")}
          </Button>
        )}
        <Button
          className="h-11 w-full sm:h-10 sm:w-auto"
          variant="light"
          onPress={onClose}
        >
          {t("library.rating.cancel")}
        </Button>
        <Button
          className="h-11 w-full font-semibold sm:h-10 sm:w-auto"
          color="warning"
          isDisabled={draft === null || draft === currentRating}
          onPress={() => {
            if (draft === null) return;
            onSave(draft);
            onClose();
          }}
        >
          {t("library.rating.save")}
        </Button>
      </ModalFooter>
    </>
  );
}
